// Create Places from Photos → "Wikipedia articles" (2026-10-17): search
// Wikipedia for a topic ("fighter aircraft", "lighthouses of Maine") and
// pick articles; each becomes a place with its photo (print size + credit)
// and opening text, with or without a map location — photos as a way to
// start a guide, not just trips. Looks like Build My Trip's place review
// (PlacesReviewModal), whose styles it reuses.
//
// Props: onCancel(), onConfirm(places, query) — the chosen Place objects
// and the search words (a new guide's name if none was typed).

import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import WikiPlaces from "../../Models/Wikipedia";
import { notesText, firstSentence } from "../../Common/placeRow";
import { creditLine, printSizeLabel, isLowResForPrint } from "../../Common/photoCredit";
import "./Modal.css";
import "./PlacesReviewModal.css";
import "./WikipediaArticlesModal.css";

const WikipediaArticlesModal = ({ onCancel, onConfirm }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = not searched yet
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [photosOnly, setPhotosOnly] = useState(true);
  // Hide articles whose photo is too small to print sharply (2026-10-18).
  const [hideLowRes, setHideLowRes] = useState(true);
  const lowRes = (p) => !!p._printSize && isLowResForPrint(p._printSize.width, p._printSize.height);

  const search = async () => {
    const q = query.trim();
    if (!q || busy) return;
    setBusy(true);
    setError("");
    const places = await new WikiPlaces().searchArticles(q, 50);
    setBusy(false);
    if (places === null) { setError("Wikipedia couldn't be reached. Please try again."); return; }
    setResults(places);
    // Ticked to start: the ones with a photo (it's a photo-first list).
    setSelected(new Set(places.filter((p) => p.Photo_m).map((p) => p.ID)));
  };

  const withPhotos = (results || []).filter((p) => !photosOnly || p.Photo_m);
  const shown = withPhotos.filter((p) => !hideLowRes || !lowRes(p));
  const lowResHidden = withPhotos.length - shown.length;
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const chosen = shown.filter((p) => selected.has(p.ID));

  return (
    <div className="modal">
      <Modal show={true} animation={false} centered dialogClassName="prw-dialog" onHide={onCancel} aria-labelledby="wam-title">
        <Modal.Body style={{ padding: 0 }}>
          <div className="prw-shell">
            <div className="prw-header">
              <h2 className="prw-title" id="wam-title">Wikipedia articles</h2>
              {results && <span className="prw-count">{chosen.length} of {shown.length} selected</span>}
              <button className="prw-close" onClick={onCancel} aria-label="Close">
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="wam-search">
              <input
                className="wam-input"
                type="search"
                value={query}
                placeholder="A topic, e.g. fighter aircraft, lighthouses of Maine"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                aria-label="Search Wikipedia"
                autoFocus
              />
              <button className="tai-primary-btn" onClick={search} disabled={busy || !query.trim()}>
                {busy ? "Searching…" : "Search"}
              </button>
            </div>
            {results && results.some((p) => !p.Photo_m) && (
              <label className="wam-photos-only">
                <input type="checkbox" checked={photosOnly} onChange={(e) => setPhotosOnly(e.target.checked)} />
                Only articles with a free photo
              </label>
            )}
            {results && results.some(lowRes) && (
              <label className="wam-photos-only">
                <input type="checkbox" checked={hideLowRes} onChange={(e) => setHideLowRes(e.target.checked)} />
                Hide low-resolution photos{lowResHidden > 0 ? ` (${lowResHidden} hidden)` : ""}
              </label>
            )}

            <div className="prw-body">
              {error && <p className="wam-note">{error}</p>}
              {!results && !error && (
                <p className="wam-note">Each article you pick becomes a place with its photo and description — with or without a map location. Photos keep their credit for a Photo Book.</p>
              )}
              {results && shown.length === 0 && <p className="wam-note">No articles found. Try other words.</p>}
              {shown.map((p) => {
                const on = selected.has(p.ID);
                const text = notesText(p.Info);
                return (
                  <div key={p.ID} className={`prw-row${on ? " on" : ""}`}>
                    <label className="prw-pick">
                      <input type="checkbox" checked={on} onChange={() => toggle(p.ID)} aria-label={`Add ${p.Name}`} />
                    </label>
                    {p.Photo_m ? <img className="prw-thumb" src={p.Photo_m} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <div className="prw-thumb prw-thumb-empty" aria-hidden="true" />}
                    <div className="prw-text" onClick={() => toggle(p.ID)}>
                      <div className="prw-name">{p.Name}</div>
                      <div className="prw-meta">
                        {[p.Lat !== "" && p.Lat != null ? "On the map" : "No map location", p.PhotoCredit ? creditLine(p.PhotoCredit) : p.Photo_m ? "" : "No free photo", p._printSize ? printSizeLabel(p._printSize.width, p._printSize.height) : ""].filter(Boolean).join(" · ")}
                      </div>
                      {text && <div className="prw-desc">{firstSentence(text.replace(/\s*\.\.\. See More\.?$/i, ""))}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="prw-footer">
              <button className="tai-cancel-btn" onClick={onCancel}>Cancel</button>
              <button className="tai-primary-btn" disabled={chosen.length === 0} onClick={() => onConfirm(chosen, query.trim())}>
                Add {chosen.length} place{chosen.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WikipediaArticlesModal;
